import type { ReactNode } from 'react'
import { Box, Button, MenuItem, TextField } from '@mui/material'
import AppDatePicker from '@/components/DatePickers/AppDatePicker'
import {
  controlFontSize,
  controlHeight,
} from '@/components/DatePickers/datePickerStyles'
import { useAppTheme } from '@/theme/ThemeContext'

export type FilterToolbarOption = {
  value: string
  label: string
  color?: string
}

export type FilterToolbarField =
  | {
      name: string
      type: 'select'
      label: string
      value: string
      options: FilterToolbarOption[]
      onChange: (value: string) => void
      width?: string
    }
  | {
      name: string
      type: 'date'
      label: string
      value: Date | null
      onChange: (value: Date | null) => void
      width?: string
      format?: string
      minDate?: Date
      maxDate?: Date
      disableFuture?: boolean
      clearable?: boolean
      onCommit?: (value: Date | null) => void
      onErrorChange?: (error: string | null) => void
    }
  | {
      name: string
      type: 'text'
      label: string
      value: string
      onChange: (value: string) => void
      width?: string
      placeholder?: string
    }
  | {
      name: string
      type: 'custom'
      render: ReactNode
    }

type FilterToolbarProps = {
  fields: FilterToolbarField[]
  onSearch: () => void
  disabled?: boolean
  hideSearch?: boolean
  compact?: boolean
  onClear?: () => void
  onMoreFilters?: () => void
  searchLabel?: string
  clearLabel?: string
  moreFiltersLabel?: string
}

const controlWidth = 'clamp(160px, 13vw, 254px)'
const actionHeight = 'clamp(30px, 2.8vw, 48px)'
const actionFontSize = 'clamp(12px, 0.9vw, 14px)'

const SlidersIcon = ({ color }: { color: string }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="8" cy="6" r="2" />
    <path d="M2 6h4m4 0h12" />
    <circle cx="16" cy="12" r="2" />
    <path d="M2 12h12m4 0h4" />
    <circle cx="9" cy="18" r="2" />
    <path d="M2 18h5m4 0h11" />
  </svg>
)

const hasValue = (value: unknown) =>
  value !== '' && value !== null && value !== undefined

export const filterToolbarFieldSx = (isDarkMode: boolean, active = true) => {
  const border = active
    ? isDarkMode
      ? '#BBD7FF'
      : '#003F61'
    : isDarkMode
      ? '#4B6FB1'
      : '#C0C2D6'
  const text = isDarkMode ? '#BBD7FF' : '#003F61'

  return {
    '--filter-outline': border,
    direction: 'rtl',
    '& .MuiOutlinedInput-root': {
      backgroundColor: isDarkMode ? '#0D192E' : '#FFF',
      color: text,
      fontFamily: 'Rubik, sans-serif',
      fontSize: controlFontSize,
      borderRadius: '4px',
      height: controlHeight,
      '& fieldset': {
        borderColor: 'var(--filter-outline) !important',
        borderWidth: '1px',
      },
      '&:hover fieldset': { borderColor: 'var(--filter-outline) !important' },
      '&.Mui-focused fieldset': {
        borderColor: `${isDarkMode ? '#BBD7FF' : '#003F61'} !important`,
        borderWidth: '1px',
      },
    },
    '& .MuiInputLabel-root': {
      color: `${text} !important`,
      fontFamily: 'Rubik, sans-serif',
      fontSize: 'clamp(13px, 0.9vw, 15px)',
      right: '14px',
      left: 'auto',
      transformOrigin: 'top right',
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(0, -7px) scale(0.75)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      direction: 'rtl',
      textAlign: 'right',
    },
    '& .MuiOutlinedInput-notchedOutline legend': {
      textAlign: 'right',
      width: 'fit-content !important',
      maxWidth: 'fit-content !important',
      marginLeft: 'auto',
      marginRight: '6px',
      fontSize: '10px',
    },
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
      fontFamily: 'Rubik, sans-serif',
      fontSize: controlFontSize,
      color: text,
    },
    '& .MuiSelect-select': {
      textAlign: 'right',
      paddingRight: '12px !important',
      paddingLeft: '32px !important',
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiSelect-icon': { left: '8px', right: 'auto', color: text },
    '& .MuiSvgIcon-root': { color: text },
  }
}

export default function FilterToolbar({
  fields,
  onSearch,
  disabled = false,
  hideSearch = false,
  compact = false,
  onClear,
  onMoreFilters,
  searchLabel = 'חיפוש',
  clearLabel = 'ניקוי סינונים',
  moreFiltersLabel = 'סינונים נוספים',
}: FilterToolbarProps) {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const text = isDarkMode ? '#BBD7FF' : '#003F61'

  const renderField = (field: FilterToolbarField) => {
    if (field.type === 'custom')
      return <Box key={field.name}>{field.render}</Box>

    const width = field.width ?? controlWidth
    const commonSx = { width, minWidth: width }

    if (field.type === 'date') {
      return (
        <AppDatePicker
          key={field.name}
          filled
          label={field.label}
          value={field.value}
          onChange={field.onChange}
          width={width}
          format={field.format}
          minDate={field.minDate}
          maxDate={field.maxDate}
          disableFuture={field.disableFuture}
          clearable={field.clearable}
          onCommit={field.onCommit}
          onErrorChange={field.onErrorChange}
        />
      )
    }

    if (field.type === 'select') {
      const selected = field.options.find(
        option => option.value === field.value
      )
      return (
        <TextField
          key={field.name}
          select
          size="small"
          label={field.label}
          value={field.value}
          onChange={event => field.onChange(event.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            ...commonSx,
            ...filterToolbarFieldSx(isDarkMode, hasValue(field.value)),
          }}
          SelectProps={{
            renderValue: () => (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  direction: 'rtl',
                }}
              >
                {selected?.color && selected.color !== 'transparent' ? (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: selected.color,
                      flexShrink: 0,
                    }}
                  />
                ) : null}
                <span>{selected?.label ?? ''}</span>
              </Box>
            ),
          }}
        >
          {field.options.map(option => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: 'flex-end',
                fontFamily: 'Rubik, sans-serif',
                fontSize: '14px',
              }}
            >
              {option.label}
              {option.color && option.color !== 'transparent' ? (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: option.color,
                  }}
                />
              ) : null}
            </MenuItem>
          ))}
        </TextField>
      )
    }

    return (
      <TextField
        key={field.name}
        size="small"
        label={field.label}
        value={field.value}
        placeholder={field.placeholder}
        onChange={event => field.onChange(event.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{
          ...commonSx,
          ...filterToolbarFieldSx(isDarkMode, hasValue(field.value)),
        }}
      />
    )
  }

  const secondaryButtonSx = {
    height: actionHeight,
    borderRadius: '4px',
    borderColor: isDarkMode ? '#43597F' : '#E4E7EB',
    color: text,
    backgroundColor: isDarkMode ? '#0D192E' : '#FFF',
    fontFamily: 'Rubik, sans-serif',
    fontWeight: 400,
    fontSize: actionFontSize,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      borderColor: isDarkMode ? '#BBD7FF' : '#C8D5E1',
      backgroundColor: isDarkMode ? '#13233F' : 'rgba(0,0,0,0.04)',
    },
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: onClear ? 'space-between' : 'flex-end',
        gap: 2,
        width: '100%',
        direction: 'ltr',
        flexWrap: { xs: 'wrap', lg: 'nowrap' },
      }}
    >
      {onClear ? (
        <Button
          variant="outlined"
          size="small"
          onClick={onClear}
          sx={{
            ...secondaryButtonSx,
            width: 'clamp(82px, 6.7vw, 132px)',
            minWidth: 'clamp(82px, 6.7vw, 132px)',
          }}
        >
          {clearLabel}
        </Button>
      ) : null}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: compact
            ? 'clamp(6px, 0.65vw, 12px)'
            : 'clamp(12px, 1.25vw, 26px)',
          direction: 'rtl',
          flexWrap: 'wrap',
        }}
      >
        {fields.map(renderField)}

        {onMoreFilters ? (
          <Button
            variant="outlined"
            size="small"
            onClick={onMoreFilters}
            sx={{
              ...secondaryButtonSx,
              width: 'clamp(106px, 8.5vw, 169px)',
              minWidth: 'clamp(106px, 8.5vw, 169px)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <span>{moreFiltersLabel}</span>
            <SlidersIcon color={text} />
          </Button>
        ) : null}

        {!hideSearch ? (
          <Button
            disabled={disabled}
            variant="contained"
            size="small"
            onClick={onSearch}
            sx={{
              width: 'clamp(68px, 5.5vw, 110px)',
              minWidth: 'clamp(68px, 5.5vw, 110px)',
              height: actionHeight,
              borderRadius: '4px',
              backgroundColor: isDarkMode ? '#0B47A8' : '#DDF3FF',
              color: isDarkMode ? '#FFFFFF' : '#003F61',
              border: `1px solid ${isDarkMode ? '#0B47A8' : '#C6E8F7'}`,
              boxShadow: 'none',
              fontFamily: 'Rubik, sans-serif',
              fontWeight: 500,
              fontSize: actionFontSize,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: isDarkMode ? '#093A8A' : '#CFEFFF',
                boxShadow: 'none',
              },
            }}
          >
            {searchLabel}
          </Button>
        ) : null}
      </Box>
    </Box>
  )
}
